/**
 * Trading-control gating (owner decision 2026-09-27 "Yes, mock + Test only"; ADR-0021,
 * SPEC-ADDENDA A2 amendment; SPEC §1.3, §6.10, §9.7, §9.9).
 *
 * Every control that places, modifies, cancels or exits asks `gateAction` whether it may run. It
 * is enabled only when **all** hold, checked in this order so the tooltip names the first thing
 * to fix:
 *
 * 1. the trading socket is connected (`NO_GATEWAY`);
 * 2. the gateway reported an order-plant session (`NO_SESSION`);
 * 3. that session's environment — derived by the gateway from its environment guard, never by
 *    the client — is MOCK or TEST, or PAPER / LIVE (a user's prop-firm or live login, ADR-0027)
 *    (`ENVIRONMENT`; UNSPECIFIED is never tradable). On PAPER / LIVE, new orders, modifies and
 *    Reverse also need the login's "Allow trading" switch on as the gateway reports it
 *    (`SessionState.trading_enabled`, `TRADING_OFF`); exits and cancels do not;
 * 4. the session is READY (`SESSION_NOT_READY`: connecting, syncing, degraded, kicked…) — except
 *    cancels, which the gateway sends as soon as the order plant is logged in, so they also work
 *    while it is SYNCING (audit L5);
 * 5. the control acts on one specific trading account (`NO_ACCOUNT`, `ALL_ACCOUNTS`,
 *    `UNKNOWN_ACCOUNT`) whose own environment is MOCK or TEST (`ENVIRONMENT`);
 * 6. for new orders: the contract and its tick size are known (`NO_INSTRUMENT`), a join has a
 *    price (`NO_PRICE`), no same-side order on the contract is UNKNOWN (`UNKNOWN_ORDER_PENDING`,
 *    SPEC §6.9), and the account's risk state does not block new orders (`RISK_CONFIG_INVALID`,
 *    `LOCKOUT` with a countdown, `HARD_STOP`) — unless the order only reduces the position
 *    (§9.9: a locked account can always get flat).
 *
 * Exits and cancels skip step 6: getting flat always has priority. Exits (Exit & Cxl, Flatten,
 * Reverse) and new orders are refused while the account's positions are not confirmed by Rithmic's
 * PnL plant (`POSITION_UNCONFIRMED`, ADR-0032: the gateway will not guess an exit, and an account
 * that cannot get flat must not add risk). Cancels stay allowed. Reverse & Cxl (SPEC §9.7)
 * needs a confirmed position: a contract with a position (`NO_POSITION`), no order on it in
 * UNKNOWN on either side (`UNKNOWN_ORDER_PENDING`), and — its second half opens a new position —
 * a risk state that allows new orders. "Busy" (a command awaiting its `CommandResult`) is tracked
 * by `TradingCommands`, not here.
 *
 * Pure, and only type-imports generated code, so it runs under `node --test`. The gateway enforces
 * all of this again (ADR-0010 guard, READY gate, risk gate); this is defence in depth and the
 * explanation the user sees.
 */

import type { TradeSide } from "./orderEntry.ts";

/** Mirrors proto `TradingEnvironment` (checked against the generated enum by the type checker). */
export const TRADING_ENV = {
  UNSPECIFIED: 0,
  MOCK: 1,
  TEST: 2,
  PAPER: 3,
  LIVE: 4,
} as const;

/** Mirrors proto `SessionStatus`. */
export const SESSION_STATUS = {
  DISCONNECTED: 1,
  CONNECTING: 2,
  SYNCING: 3,
  READY: 4,
  DEGRADED: 5,
  KICKED: 6,
} as const;

/** Mirrors proto `RiskStatus` (the values that block new orders). */
export const RISK_STATUS = { BREACHED: 4, LOCKED: 5, FIRM_BREACHED: 6 } as const;

const BLOCKING_RISK_STATUS: ReadonlySet<number> = new Set<number>([
  RISK_STATUS.BREACHED,
  RISK_STATUS.LOCKED,
  RISK_STATUS.FIRM_BREACHED,
]);

/** The local mock or Rithmic Test: simulated, no switch (ADR-0010, ADR-0021). */
export function isSimulatedEnvironment(env: number | null | undefined): boolean {
  return env === TRADING_ENV.MOCK || env === TRADING_ENV.TEST;
}

/** A paper / prop-firm or live login: real routing, governed by the owner switch (ADR-0027). */
export function isRealEnvironment(env: number | null | undefined): boolean {
  return env === TRADING_ENV.PAPER || env === TRADING_ENV.LIVE;
}

/**
 * May new orders go to this environment? Mock / Test always; PAPER / LIVE only with the login's
 * "Allow trading" switch on (as the gateway reports it). UNSPECIFIED never.
 */
export function isTradableEnvironment(env: number | null | undefined, tradingEnabled = false): boolean {
  return isSimulatedEnvironment(env) || (isRealEnvironment(env) && tradingEnabled);
}

/** What the UI shows while a real login is connected with its switch off (ADR-0032 amendment). */
export const REDUCE_ONLY_LABEL = "Trading off — exits & cancels only";

/**
 * A READY paper / prop / live login whose "Allow trading" switch is off: its order plant is
 * connected **reduce-only** (ADR-0032 amendment, audit L4) — cancels and exits (Exit & Cxl,
 * Flatten) go out, anything that opens or adds is refused by the gate and the gateway.
 */
export function isReduceOnly(
  session: { status: number; environment: number; tradingEnabled?: boolean } | null | undefined,
): boolean {
  return (
    session != null &&
    session.status === SESSION_STATUS.READY &&
    isRealEnvironment(session.environment) &&
    session.tradingEnabled !== true
  );
}

export function environmentLabel(env: number | null | undefined): string {
  switch (env) {
    case TRADING_ENV.MOCK:
      return "mock";
    case TRADING_ENV.TEST:
      return "Rithmic Test";
    case TRADING_ENV.PAPER:
      return "paper / prop";
    case TRADING_ENV.LIVE:
      return "LIVE";
    default:
      return "unknown";
  }
}

export type GateCode =
  | "NO_GATEWAY"
  | "NO_SESSION"
  | "ENVIRONMENT"
  | "SESSION_NOT_READY"
  | "NO_ACCOUNT"
  | "ALL_ACCOUNTS"
  | "UNKNOWN_ACCOUNT"
  | "NO_INSTRUMENT"
  | "NO_PRICE"
  | "UNKNOWN_ORDER_PENDING"
  | "RISK_CONFIG_INVALID"
  | "LOCKOUT"
  | "HARD_STOP"
  | "NO_POSITION"
  | "NOT_AVAILABLE"
  | "TRADING_OFF"
  | "POSITION_UNCONFIRMED";

export type Gate = { ok: true } | { ok: false; code: GateCode; reason: string };

export const GATE_OK: Gate = { ok: true };

/**
 * The parts of a `RiskState` the gate reads. `blocksNewOrders` / `configError` are optional
 * because older gateways do not send them; status and lockout are then used alone.
 */
export type RiskGateInput = {
  status: number;
  lockoutUntilMs: bigint | number;
  blocksNewOrders?: boolean;
  configError?: string;
};

export type GateContext = {
  /** The trading socket is connected (Welcome received). */
  gatewayConnected: boolean;
  /** Latest order-plant `SessionState`, or null before the gateway sent one. `tradingEnabled`:
   * the login's owner switch as the gateway last read it (PAPER / LIVE only, ADR-0027). */
  session: { status: number; environment: number; detail: string; tradingEnabled?: boolean } | null;
  /** Known accounts → their `AccountUpdate.environment` (UNSPECIFIED when only seen on an order). */
  accounts: ReadonlyMap<string, number>;
  /** Risk state per account (absent: no RiskState received; the gateway still decides). */
  risk: ReadonlyMap<string, RiskGateInput>;
  nowMs: number;
  /** Accounts whose positions the gateway flags unconfirmed (`POSITIONS_UNCONFIRMED` notice or
   * `PositionUpdate.mismatch`, ADR-0032). Optional: older gateways do not flag. */
  unconfirmedAccounts?: ReadonlySet<string>;
};

/** What a control wants to do. `accountId` is the account it acts on, or "all" / null. */
export type GateAction =
  | {
      kind: "place";
      accountId: string | null;
      side: TradeSide;
      qty: number;
      /** Resolved contract (e.g. "NQZ6"), null until reference data arrives. */
      contract: string | null;
      tickKnown: boolean;
      /** True for joins / DOM clicks that need a price; then `price` must be non-null. */
      needsPrice?: boolean;
      price?: number | null;
      /** Signed net position of (account, contract). */
      netQty: number;
      /** Same-side orders on (account, contract) in state UNKNOWN (SPEC §6.9). */
      unknownSides: { readonly buy: boolean; readonly sell: boolean };
    }
  | { kind: "modify" | "cancel"; accountId: string | null }
  | { kind: "exitSymbol"; accountId: string | null; contract: string | null }
  | { kind: "cancelAll" | "flattenAccount"; accountId: string | null }
  | { kind: "flattenAll" }
  | {
      kind: "reverse";
      accountId: string | null;
      contract: string | null;
      /** Signed net position of (account, contract). */
      netQty: number;
      /** Orders on (account, contract) in state UNKNOWN, by side. */
      unknownSides: { readonly buy: boolean; readonly sell: boolean };
    };

const SESSION_WORDS: Readonly<Record<number, string>> = {
  [SESSION_STATUS.DISCONNECTED]: "disconnected",
  [SESSION_STATUS.CONNECTING]: "connecting",
  [SESSION_STATUS.SYNCING]: "syncing",
  [SESSION_STATUS.DEGRADED]: "degraded — reconnecting",
  [SESSION_STATUS.KICKED]: "logged out by Rithmic",
};

const no = (code: GateCode, reason: string): Gate => ({ ok: false, code, reason });

/** "1:02:03" / "4:05" for a remaining duration. */
export function formatCountdown(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return h > 0 ? `${String(h)}:${pad(m)}:${pad(sec)}` : `${String(m)}:${pad(sec)}`;
}

/** Why the account's risk state blocks new orders, or null. */
export function riskBlock(risk: RiskGateInput | undefined, nowMs: number): Gate | null {
  if (risk === undefined) return null;
  if (risk.configError) {
    return no(
      "RISK_CONFIG_INVALID",
      `Risk settings are invalid (${risk.configError}): new orders are blocked until they are fixed; exit and cancel availability is checked separately`,
    );
  }
  const until = Number(risk.lockoutUntilMs);
  if (until > nowMs) {
    return no(
      "LOCKOUT",
      `Lockout active — ${formatCountdown(until - nowMs)} left: new orders are blocked; exit and cancel availability is checked separately`,
    );
  }
  if (risk.blocksNewOrders === true || BLOCKING_RISK_STATUS.has(risk.status)) {
    return no(
      "HARD_STOP",
      "Risk hard stop hit: new orders are blocked until the trading-day reset; exit and cancel availability is checked separately",
    );
  }
  return null;
}

/** Does this order only reduce the position (SPEC §9.9 step 0, client-side approximation)? */
export function isReducing(side: TradeSide, qty: number, netQty: number): boolean {
  return side === "sell" ? netQty > 0 && qty <= netQty : netQty < 0 && qty <= -netQty;
}

/** Actions that add or change exposure: they need the switch on a PAPER / LIVE login. */
function opensExposure(action: GateAction): boolean {
  return action.kind === "place" || action.kind === "modify" || action.kind === "reverse";
}

function tradingOff(env: number): Gate {
  return no(
    "TRADING_OFF",
    `Trading is off for this Rithmic login (${environmentLabel(env)}): turn on "Allow trading" in Settings → Rithmic. Exit and cancel availability is checked separately`,
  );
}

function sessionGate(ctx: GateContext, action: GateAction): Gate | null {
  if (!ctx.gatewayConnected) return no("NO_GATEWAY", "Not connected to the gateway");
  const s = ctx.session;
  if (s === null) return no("NO_SESSION", "No Rithmic order plant for this session");
  if (!isSimulatedEnvironment(s.environment) && !isRealEnvironment(s.environment)) {
    return no(
      "ENVIRONMENT",
      s.detail ? `No order plant: ${s.detail}` : "Order plant environment unknown: order entry is disabled",
    );
  }
  if (isRealEnvironment(s.environment) && s.tradingEnabled !== true && opensExposure(action)) {
    return tradingOff(s.environment);
  }
  const cancelWhileSyncing =
    s.status === SESSION_STATUS.SYNCING && (action.kind === "cancel" || action.kind === "cancelAll");
  if (s.status !== SESSION_STATUS.READY && !cancelWhileSyncing) {
    const words = SESSION_WORDS[s.status] ?? "not ready";
    return no(
      "SESSION_NOT_READY",
      `Order plant ${words}${s.detail ? ` (${s.detail})` : ""}: order entry is disabled until READY`,
    );
  }
  return null;
}

function accountGate(ctx: GateContext, accountId: string | null, action: GateAction): Gate | null {
  if (accountId === null) return no("NO_ACCOUNT", "No trading account");
  if (accountId === "all") {
    return no(
      "ALL_ACCOUNTS",
      "Select one trading account in the top bar (All accounts is view-only for orders)",
    );
  }
  const env = ctx.accounts.get(accountId);
  if (env === undefined) return no("UNKNOWN_ACCOUNT", `Account ${accountId} is not on this login`);
  if (!isSimulatedEnvironment(env) && !isRealEnvironment(env)) {
    return no("ENVIRONMENT", `Account ${accountId}: environment unknown, order entry is disabled`);
  }
  if (isRealEnvironment(env) && ctx.session?.tradingEnabled !== true && opensExposure(action)) {
    return tradingOff(env);
  }
  return null;
}

function unconfirmed(id: string): Gate {
  return no(
    "POSITION_UNCONFIRMED",
    `Positions on ${id} are not confirmed by Rithmic: TradePrizm will not guess an exit and refuses new orders — trade this account on your broker platform (R|Trader)`,
  );
}

/** May `action` run now? `{ ok: true }`, or the first reason it may not. */
export function gateAction(ctx: GateContext, action: GateAction): Gate {
  const session = sessionGate(ctx, action);
  if (session) return session;
  if (action.kind === "flattenAll") {
    if (ctx.accounts.size === 0) return no("NO_ACCOUNT", "No trading accounts");
    for (const [id, env] of ctx.accounts) {
      if (!isSimulatedEnvironment(env) && !isRealEnvironment(env)) {
        return no("ENVIRONMENT", `Account ${id}: environment unknown`);
      }
      if (ctx.unconfirmedAccounts?.has(id) === true) return unconfirmed(id);
    }
    return GATE_OK;
  }
  const account = accountGate(ctx, action.accountId, action);
  if (account) return account;
  // Exits: the gateway will not guess one. New orders: an account whose position is unknown must
  // not add risk while it cannot remove it (launch-readiness B3; the gateway refuses both).
  const refusedUnconfirmed =
    action.kind === "exitSymbol" ||
    action.kind === "flattenAccount" ||
    action.kind === "reverse" ||
    action.kind === "place";
  if (
    refusedUnconfirmed &&
    action.accountId !== null &&
    ctx.unconfirmedAccounts?.has(action.accountId) === true
  ) {
    return unconfirmed(action.accountId);
  }
  if (action.kind === "exitSymbol" && action.contract === null) {
    return no("NO_INSTRUMENT", "Waiting for the contract's reference data");
  }
  if (action.kind === "reverse") return reverseGate(ctx, action);
  if (action.kind !== "place") return GATE_OK;
  if (action.contract === null || !action.tickKnown) {
    return no("NO_INSTRUMENT", "Waiting for the contract's reference data (tick size)");
  }
  if (action.needsPrice === true && (action.price === null || action.price === undefined)) {
    return no("NO_PRICE", "No best bid/ask yet");
  }
  if (action.unknownSides[action.side]) {
    return no(
      "UNKNOWN_ORDER_PENDING",
      `A ${action.side} order on ${action.contract} is being reconciled (UNKNOWN): same-side orders are blocked until it resolves`,
    );
  }
  if (!isReducing(action.side, action.qty, action.netQty)) {
    const risk = riskBlock(ctx.risk.get(action.accountId ?? ""), ctx.nowMs);
    if (risk) return risk;
  }
  return GATE_OK;
}

/** Reverse & Cxl after the session and account gates (SPEC §9.7: never on an unconfirmed N). */
function reverseGate(ctx: GateContext, a: Extract<GateAction, { kind: "reverse" }>): Gate {
  if (a.contract === null) return no("NO_INSTRUMENT", "Waiting for the contract's reference data");
  if (a.netQty === 0) return no("NO_POSITION", `No ${a.contract} position to reverse`);
  if (a.unknownSides.buy || a.unknownSides.sell) {
    return no(
      "UNKNOWN_ORDER_PENDING",
      `An order on ${a.contract} is being reconciled (UNKNOWN): reverse needs a confirmed position`,
    );
  }
  // The reverse opens |N| on the other side: the same risk blocks as a new order.
  return riskBlock(ctx.risk.get(a.accountId ?? ""), ctx.nowMs) ?? GATE_OK;
}

function sameMap<V>(
  a: ReadonlyMap<string, V>,
  b: ReadonlyMap<string, V>,
  eq: (x: V, y: V) => boolean,
): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [k, v] of a) {
    const w = b.get(k);
    if (w === undefined || !eq(v, w)) return false;
  }
  return true;
}

/**
 * Two contexts every gate answers the same for (field by field). A RiskState frame carries P&L
 * and room too — many a second with a position open — but changes a gate only through these
 * fields, so a control can keep its context (and skip a render) while this holds.
 */
export function sameGateContext(a: GateContext, b: GateContext): boolean {
  if (a === b) return true;
  const as = a.session;
  const bs = b.session;
  const au = a.unconfirmedAccounts;
  const bu = b.unconfirmedAccounts;
  return (
    a.gatewayConnected === b.gatewayConnected &&
    a.nowMs === b.nowMs &&
    (as === null || bs === null
      ? as === bs
      : as.status === bs.status &&
        as.environment === bs.environment &&
        as.detail === bs.detail &&
        as.tradingEnabled === bs.tradingEnabled) &&
    sameMap(a.accounts, b.accounts, (x, y) => x === y) &&
    sameMap(
      a.risk,
      b.risk,
      (x, y) =>
        x.status === y.status &&
        Number(x.lockoutUntilMs) === Number(y.lockoutUntilMs) &&
        x.blocksNewOrders === y.blocksNewOrders &&
        x.configError === y.configError,
    ) &&
    (au === undefined || bu === undefined
      ? au === bu
      : au.size === bu.size && [...au].every((id) => bu.has(id)))
  );
}

/** The earliest future instant at which a gate could change by itself (lockout end), or null. */
export function nextGateChangeMs(ctx: GateContext): number | null {
  let next: number | null = null;
  for (const r of ctx.risk.values()) {
    const until = Number(r.lockoutUntilMs);
    if (until > ctx.nowMs && (next === null || until < next)) next = until;
  }
  return next;
}
